import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { LogOut, Mail, Phone, Building2, Calendar, Search, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import NidaLogo from "../components/NidaLogo";
import { useI18n } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
    new: "bg-brand/15 text-brand-glow border-brand/30",
    contacted: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    qualified: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    won: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
    lost: "bg-white/5 text-white/40 border-white/10",
};

export default function AdminDashboard() {
    const { t, dict } = useI18n();
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, qualified: 0, won: 0, lost: 0 });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(null);
    const navigate = useNavigate();

    const token = typeof window !== "undefined" ? localStorage.getItem("nida_admin_token") : null;

    const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

    useEffect(() => {
        if (!token) {
            navigate("/admin/login");
            return;
        }
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const [leadsRes, statsRes] = await Promise.all([
                axios.get(`${API}/admin/leads`, { headers }),
                axios.get(`${API}/admin/stats`, { headers }),
            ]);
            setLeads(leadsRes.data);
            setStats(statsRes.data);
        } catch (err) {
            if (err?.response?.status === 401) {
                localStorage.removeItem("nida_admin_token");
                navigate("/admin/login");
            }
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (id, status) => {
        try {
            await axios.patch(`${API}/admin/leads/${id}`, { status }, { headers });
            toast.success("Estado actualizado");
            load();
            setSelected((s) => (s && s.id === id ? { ...s, status } : s));
        } catch {
            toast.error("Error al actualizar");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("¿Eliminar este lead?")) return;
        try {
            await axios.delete(`${API}/admin/leads/${id}`, { headers });
            toast.success("Lead eliminado");
            setSelected(null);
            load();
        } catch {
            toast.error("Error al eliminar");
        }
    };

    const logout = () => {
        localStorage.removeItem("nida_admin_token");
        navigate("/admin/login");
    };

    const filtered = leads.filter((l) => {
        if (filter !== "all" && l.status !== filter) return false;
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return (
            l.name?.toLowerCase().includes(q) ||
            l.email?.toLowerCase().includes(q) ||
            l.company?.toLowerCase().includes(q) ||
            l.message?.toLowerCase().includes(q)
        );
    });

    const statuses = ["new", "contacted", "qualified", "won", "lost"];

    return (
        <div data-testid="admin-dashboard-page" className="min-h-screen bg-ink-950 text-white">
            {/* Header */}
            <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/[0.06]">
                <div className="container-page h-16 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link to="/" className="flex items-center gap-3">
                            <NidaLogo className="h-6" />
                        </Link>
                        <span className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-white/55">
                            CRM · Admin
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link
                            to="/"
                            data-testid="admin-back-home"
                            className="hidden md:inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-white"
                        >
                            <ExternalLink className="h-3.5 w-3.5" /> Web pública
                        </Link>
                        <button
                            onClick={logout}
                            data-testid="admin-logout"
                            className="btn-ghost text-xs px-4 py-2"
                        >
                            <LogOut className="h-3.5 w-3.5" />
                            {t("admin.logout")}
                        </button>
                    </div>
                </div>
            </header>

            <main className="container-page py-10 md:py-12">
                <div>
                    <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">{t("admin.title")}</h1>
                    <p className="mt-2 text-white/55 text-sm">{t("admin.subtitle")}</p>
                </div>

                {/* Stats */}
                <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                    <StatCard label="Total" value={stats.total} testid="stat-total" highlight />
                    {statuses.map((s) => (
                        <StatCard
                            key={s}
                            testid={`stat-${s}`}
                            label={dict.admin.statuses[s]}
                            value={stats[s] || 0}
                            color={STATUS_COLORS[s]}
                        />
                    ))}
                </div>

                {/* Filters */}
                <div className="mt-8 flex flex-col md:flex-row md:items-center gap-4">
                    <div className="relative flex-1 max-w-md">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                        <input
                            data-testid="admin-search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Buscar por nombre, email, empresa…"
                            className="w-full rounded-lg border border-white/10 bg-white/[0.03] pl-9 pr-4 py-2.5 text-sm outline-none focus:border-brand transition"
                        />
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <FilterButton active={filter === "all"} onClick={() => setFilter("all")}>
                            Todos
                        </FilterButton>
                        {statuses.map((s) => (
                            <FilterButton
                                key={s}
                                testid={`filter-${s}`}
                                active={filter === s}
                                onClick={() => setFilter(s)}
                            >
                                {dict.admin.statuses[s]}
                            </FilterButton>
                        ))}
                    </div>
                </div>

                {/* Table */}
                <div className="mt-6 rounded-2xl border border-white/[0.06] overflow-hidden bg-ink-900">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="leads-table">
                            <thead>
                                <tr className="border-b border-white/[0.06] text-left text-xs uppercase tracking-wider text-white/45">
                                    <th className="px-5 py-3 font-medium">Lead</th>
                                    <th className="px-5 py-3 font-medium">Empresa</th>
                                    <th className="px-5 py-3 font-medium">Servicio</th>
                                    <th className="px-5 py-3 font-medium">Estado</th>
                                    <th className="px-5 py-3 font-medium">Fecha</th>
                                    <th className="px-5 py-3 font-medium" />
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr>
                                        <td colSpan={6} className="px-5 py-12 text-center text-white/40">
                                            Cargando…
                                        </td>
                                    </tr>
                                )}
                                {!loading && filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="px-5 py-16 text-center text-white/40">
                                            {t("admin.empty")}
                                        </td>
                                    </tr>
                                )}
                                {!loading &&
                                    filtered.map((l) => (
                                        <tr
                                            key={l.id}
                                            data-testid={`lead-row-${l.id}`}
                                            onClick={() => setSelected(l)}
                                            className="border-b border-white/[0.04] hover:bg-white/[0.02] cursor-pointer transition-colors"
                                        >
                                            <td className="px-5 py-4">
                                                <div className="font-medium text-white">{l.name}</div>
                                                <div className="text-xs text-white/45">{l.email}</div>
                                            </td>
                                            <td className="px-5 py-4 text-white/70">
                                                {l.company || <span className="text-white/30">—</span>}
                                            </td>
                                            <td className="px-5 py-4 text-white/70">
                                                {l.service_interest || <span className="text-white/30">—</span>}
                                            </td>
                                            <td className="px-5 py-4">
                                                <span
                                                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] uppercase tracking-wider ${
                                                        STATUS_COLORS[l.status] || STATUS_COLORS.new
                                                    }`}
                                                >
                                                    {dict.admin.statuses[l.status] || l.status}
                                                </span>
                                            </td>
                                            <td className="px-5 py-4 text-white/55 text-xs">
                                                {new Date(l.created_at).toLocaleDateString()}
                                            </td>
                                            <td className="px-5 py-4 text-right">
                                                <span className="text-white/30 text-xs">Ver →</span>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>

            {/* Detail drawer */}
            {selected && (
                <div
                    data-testid="lead-detail-drawer"
                    className="fixed inset-0 z-50 flex"
                    onClick={() => setSelected(null)}
                >
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="relative ml-auto h-full w-full max-w-md bg-ink-900 border-l border-white/[0.06] overflow-y-auto"
                    >
                        <div className="p-6 md:p-8 space-y-6">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <h2 className="text-2xl font-semibold tracking-tight">{selected.name}</h2>
                                    <p className="text-sm text-white/55 mt-1">{selected.company || "—"}</p>
                                </div>
                                <button
                                    onClick={() => setSelected(null)}
                                    className="text-white/55 hover:text-white text-2xl leading-none"
                                    data-testid="close-lead-detail"
                                >
                                    ×
                                </button>
                            </div>

                            <div className="space-y-3 text-sm">
                                <Row icon={Mail} label="Email" value={selected.email} link={`mailto:${selected.email}`} />
                                {selected.phone && <Row icon={Phone} label="Teléfono" value={selected.phone} link={`tel:${selected.phone}`} />}
                                {selected.company && <Row icon={Building2} label="Empresa" value={selected.company} />}
                                <Row
                                    icon={Calendar}
                                    label="Recibido"
                                    value={new Date(selected.created_at).toLocaleString()}
                                />
                            </div>

                            <div>
                                <h3 className="text-xs uppercase tracking-wider text-white/45 mb-2">Servicio</h3>
                                <p className="text-sm text-white/80">{selected.service_interest || "—"}</p>
                            </div>

                            <div>
                                <h3 className="text-xs uppercase tracking-wider text-white/45 mb-2">Mensaje</h3>
                                <p className="text-sm text-white/80 leading-relaxed whitespace-pre-line bg-white/[0.02] border border-white/[0.06] rounded-lg p-4">
                                    {selected.message}
                                </p>
                            </div>

                            <div>
                                <h3 className="text-xs uppercase tracking-wider text-white/45 mb-2">Estado</h3>
                                <div className="grid grid-cols-2 gap-2">
                                    {statuses.map((s) => (
                                        <button
                                            key={s}
                                            data-testid={`status-${s}`}
                                            onClick={() => updateStatus(selected.id, s)}
                                            className={`rounded-lg border px-3 py-2 text-xs uppercase tracking-wider transition ${
                                                selected.status === s
                                                    ? STATUS_COLORS[s] + " ring-1 ring-white/15"
                                                    : "border-white/10 bg-white/[0.02] text-white/60 hover:bg-white/[0.05]"
                                            }`}
                                        >
                                            {dict.admin.statuses[s]}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <button
                                onClick={() => remove(selected.id)}
                                data-testid="delete-lead"
                                className="inline-flex items-center gap-2 text-xs text-red-300 hover:text-red-200"
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                                Eliminar lead
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function StatCard({ label, value, color, highlight, testid }) {
    return (
        <div
            data-testid={testid}
            className={`relative rounded-xl border border-white/[0.06] bg-ink-900 p-4 overflow-hidden`}
        >
            {highlight && (
                <span className="absolute top-0 left-3 right-3 h-px bg-gradient-to-r from-transparent via-brand to-transparent" />
            )}
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/45">{label}</p>
            <p className={`mt-2 text-2xl font-display font-semibold ${color ? color.split(" ")[1] : ""}`}>
                {value}
            </p>
        </div>
    );
}

function FilterButton({ children, active, onClick, testid }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className={`rounded-full border px-4 py-2 text-xs transition ${
                active
                    ? "border-white bg-white text-black"
                    : "border-white/10 bg-white/[0.02] text-white/60 hover:bg-white/[0.05]"
            }`}
        >
            {children}
        </button>
    );
}

function Row({ icon: Icon, label, value, link }) {
    const content = (
        <span className="flex items-center gap-3">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-white/10 bg-white/[0.03] text-white/60">
                <Icon className="h-3.5 w-3.5" />
            </span>
            <span className="flex-1 min-w-0">
                <span className="block text-[10px] uppercase tracking-[0.2em] text-white/40">{label}</span>
                <span className="block text-white truncate">{value}</span>
            </span>
        </span>
    );
    return link ? (
        <a href={link} className="block hover:opacity-80 transition">
            {content}
        </a>
    ) : (
        <div>{content}</div>
    );
}
